/**
 * Operations Panel - Floating sidebar showing all active operations
 * 
 * Provides real-time monitoring of long-running tasks like:
 * - VPC cleanup
 * - Instance provisioning
 * - Infrastructure deployment
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ChevronRight,
  Activity,
  Minimize2,
  Maximize2,
} from 'lucide-react';
import { operationsApi, OperationStatus } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

interface OperationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function OperationsPanel({ isOpen, onClose }: OperationsPanelProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isMinimized, setIsMinimized] = useState(false);

  // Fetch active operations with auto-refresh
  const { data: operations = [], isLoading } = useQuery({
    queryKey: ['operations-active'],
    queryFn: operationsApi.listActive,
    refetchInterval: (query) => {
      // CRITICAL FIX: Stop polling when no active operations to prevent render thrash
      const hasActive = query.state.data?.some(op => 
        ['running', 'queued', 'blocked'].includes(op.status)
      );
      return hasActive ? 3000 : false; // Stop polling when all operations complete
    },
    enabled: isOpen,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => operationsApi.cancel(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['operations-active'] });
      if (result.success) {
        toast.success('Operation cancelled');
      } else {
        toast.error(result.message || 'Failed to cancel');
      }
    },
    onError: () => {
      toast.error('Failed to cancel operation');
    },
  });

  const getStatusIcon = (status: OperationStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'queued':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'blocked':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'partial':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'cancelled':
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
      case 'rolled_back':
        return <AlertCircle className="w-5 h-5 text-purple-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const activeCount = operations.filter(op => ['running', 'queued', 'blocked'].includes(op.status)).length;
  const completedCount = operations.filter(op => op.status === 'completed').length;
  const failedCount = operations.filter(op => op.status === 'failed').length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ 
              x: 0, 
              opacity: 1,
              width: isMinimized ? 320 : 600,
            }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ 
              type: 'spring', 
              damping: 25, 
              stiffness: 200,
              width: { type: 'spring', damping: 30, stiffness: 300 }
            }}
            className={clsx(
              'fixed right-0 top-0 h-full bg-surface-900 border-l border-surface-800 shadow-2xl z-50',
              'flex flex-col overflow-hidden'
            )}
            style={{ minWidth: isMinimized ? 320 : 600 }}
          >
            {/* Header */}
            <div className="p-4 border-b border-surface-800 bg-surface-900/80 backdrop-blur-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-500" />
                  <h2 className="text-lg font-semibold text-white">Operations</h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsMinimized(!isMinimized)}
                    className="p-1.5 hover:bg-surface-800 rounded transition"
                    title={isMinimized ? 'Expand' : 'Minimize'}
                  >
                    {isMinimized ? (
                      <Maximize2 className="w-4 h-4 text-surface-400" />
                    ) : (
                      <Minimize2 className="w-4 h-4 text-surface-400" />
                    )}
                  </button>
                  <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-surface-800 rounded transition"
                  >
                    <X className="w-5 h-5 text-surface-400" />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  {/* CRITICAL FIX: Only show spinner when operations are actually active */}
                  {activeCount > 0 ? (
                    <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                  ) : (
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  )}
                  <span className="text-surface-400">Active:</span>
                  <span className="font-semibold text-white">{activeCount}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-surface-400">Completed:</span>
                  <span className="font-semibold text-white">{completedCount}</span>
                </div>
                {failedCount > 0 && (
                  <div className="flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5 text-red-500" />
                    <span className="text-surface-400">Failed:</span>
                    <span className="font-semibold text-white">{failedCount}</span>
                  </div>
                )}
              </div>

              {/* View All Link */}
              <button
                onClick={() => {
                  navigate('/operations');
                  onClose();
                }}
                className="mt-3 text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
              >
                View all operations
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Operations List */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-surface-500" />
                </div>
              ) : operations.length === 0 ? (
                <div className="text-center py-12 text-surface-500">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No active operations</p>
                  <p className="text-xs mt-2 text-surface-600">Operations will appear here when you create containers, scale clusters, or perform other infrastructure tasks.</p>
                </div>
              ) : (
                operations.map((operation) => (
                  <motion.div
                    key={operation.id}
                    // CRITICAL FIX: Remove 'layout' prop to prevent render thrash on operation completion
                    // The layout animations + continuous polling caused UI deadlock when operations completed
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className={clsx(
                      'p-4 rounded-lg border transition-all',
                      operation.status === 'running'
                        ? 'bg-blue-500/5 border-blue-500/20'
                        : operation.status === 'completed'
                        ? 'bg-green-500/5 border-green-500/20'
                        : operation.status === 'failed'
                        ? 'bg-red-500/5 border-red-500/20'
                        : operation.status === 'blocked'
                        ? 'bg-amber-500/5 border-amber-500/20'
                        : 'bg-surface-800/50 border-surface-700'
                    )}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        {getStatusIcon(operation.status)}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white text-sm truncate">
                            {operation.title}
                          </h3>
                          <p className="text-xs text-surface-400 mt-0.5">
                            {formatDistanceToNow(new Date(operation.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      {['running', 'queued', 'blocked'].includes(operation.status) && (
                        <button
                          onClick={() => {
                            if (confirm('Cancel this operation?')) {
                              cancelMutation.mutate(operation.id);
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/10 transition"
                        >
                          Cancel
                        </button>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {operation.status === 'running' && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-surface-300 truncate max-w-[70%]">
                            {operation.currentPhase || 'Processing...'}
                          </span>
                          <span className="font-medium text-blue-400">
                            {operation.progress}%
                          </span>
                        </div>
                        <div className="w-full bg-surface-800 rounded-full h-1.5 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${operation.progress}%` }}
                            transition={{ duration: 0.5 }}
                            className="bg-blue-500 h-1.5 rounded-full"
                          />
                        </div>
                      </div>
                    )}

                    {/* Resource IDs (compact) */}
                    {!isMinimized && operation.targetResources && (
                      <div className="text-xs text-surface-400 space-y-1 mb-2">
                        {operation.targetResources.vpcId && (
                          <div className="flex items-center gap-2">
                            <span className="text-surface-500">VPC:</span>
                            <code className="bg-surface-800 px-1.5 py-0.5 rounded font-mono">
                              {operation.targetResources.vpcId}
                            </code>
                          </div>
                        )}
                        {operation.targetResources.instanceId && (
                          <div className="flex items-center gap-2">
                            <span className="text-surface-500">Instance:</span>
                            <code className="bg-surface-800 px-1.5 py-0.5 rounded font-mono">
                              {operation.targetResources.instanceId}
                            </code>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Blocking Factors */}
                    {operation.blockingFactors && operation.blockingFactors.length > 0 && (
                      <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-400">
                        <span className="font-medium">Blocked:</span> {operation.blockingFactors[0].description}
                      </div>
                    )}

                    {/* Error Message */}
                    {operation.errorMessage && (
                      <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                        {operation.errorMessage}
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
